import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Clock,
  Calendar,
  Target,
  User,
  DollarSign,
  FileText,
  CheckCircle,
  AlertTriangle,
  Plus,
  X,
  Receipt,
  CreditCard,
  Banknote,
  Building,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const CreateInvoice = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  
  // Form state
  const [formData, setFormData] = useState({
    // Invoice Information
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    status: 'draft',
    
    // Client Information
    client: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientAddress: '',
    
    // Project Information
    project: '',
    description: '',
    
    // Billing Details
    items: [],
    
    // Financial Information
    subtotal: 0,
    taxRate: 0,
    taxAmount: 0,
    total: 0,
    
    // Additional Information
    notes: '',
    terms: 'Payment due within 30 days of invoice date.',
    paymentMethods: ['Bank Transfer', 'Check', 'Credit Card']
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectsResponse, customersResponse] = await Promise.all([
          fetch(API_CONFIG.ENDPOINTS.PROJECTS, { headers: API_CONFIG.HEADERS }),
          fetch(API_CONFIG.ENDPOINTS.CUSTOMERS, { headers: API_CONFIG.HEADERS })
        ]);

        if (projectsResponse.ok) {
          const data = await projectsResponse.json();
          
          const projectsArray =
          Array.isArray(data?.data?.projects)
          ? data.data.projects
          : Array.isArray(data?.projects)
          ? data.projects
          : [];
          
          setProjects(projectsArray);
        }

        if (customersResponse.ok) {
          const data = await customersResponse.json();
          const customers = data.data || [];
          setClients(customers.map((customer) => ({
            id: customer._id || customer.id,
            name: customer.name,
            email: customer.primaryContact?.email || '',
            phone: customer.primaryContact?.phone || '',
            address: customer.addresses?.billing?.street || customer.addresses?.headquarters?.street || ''
          })));
        }
      } catch (err) {
        showError('Failed to load projects or clients');
      }
    };

    loadData();
    
    // Set default due date (30 days from today)
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 30);
    setFormData(prev => ({
      ...prev,
      dueDate: dueDate.toISOString().split('T')[0]
    }));
    
    // Generate invoice number
    const invoiceNumber = `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`;
    setFormData(prev => ({
      ...prev,
      invoiceNumber
    }));
  }, []);

  // Initialize with default item
  useEffect(() => {
    if (formData.items.length === 0) {
      setFormData(prev => ({
        ...prev,
        items: [{
          id: 1,
          description: '',
          quantity: 1,
          rate: 75,
          amount: 75
        }]
      }));
    }
  }, []);

  // Calculate totals when items change
  useEffect(() => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxAmount = subtotal * (formData.taxRate / 100);
    const total = subtotal + taxAmount;
    
    setFormData(prev => ({
      ...prev,
      subtotal,
      taxAmount,
      total
    }));
  }, [formData.items, formData.taxRate]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle client selection
  const handleClientChange = (clientId) => {
    const selectedClient = clients.find(c => c.id === clientId);
    if (selectedClient) {
      setFormData(prev => ({
        ...prev,
        client: clientId,
        clientName: selectedClient.name,
        clientEmail: selectedClient.email,
        clientPhone: selectedClient.phone,
        clientAddress: selectedClient.address
      }));
    }
  };

  // Handle item changes
  const handleItemChange = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'rate') {
            updatedItem.amount = updatedItem.quantity * updatedItem.rate;
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  // Add new item
  const handleAddItem = () => {
    const newItem = {
      id: Date.now(),
      description: '',
      quantity: 1,
      rate: 75,
      amount: 75
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  // Remove item
  const handleRemoveItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const selectedClient = clients.find(c => c.id === formData.client);
      const selectedProject = Array.isArray(projects)
  ? projects.find(p => (p._id || p.id) === formData.project)
  : null;

      const payload = {
        ...formData,
        clientName: selectedClient?.name || '',
        clientEmail: selectedClient?.email || '',
        clientPhone: selectedClient?.phone || '',
        clientAddress: selectedClient?.address || '',
        project: selectedProject?._id || selectedProject?.id,
        projectName: selectedProject?.name || ''
      };

      const response = await fetch(API_CONFIG.ENDPOINTS.PROJECT_INVOICES, {
        method: 'POST',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create invoice');
      }

      success('Invoice created successfully!');
      navigate('/projects/time-tracking/billing');
    } catch (err) {
      console.error('Error creating invoice:', err);
      showError(err.message || 'Failed to create invoice. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Create Invoice"
        description="Create and manage invoices for time tracking and project billing"
      />
    <style>
      {`
        .invoice-tabs-container {
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 16px;
        }

        .invoice-tabs {
          display: flex;
          gap: 6px;
          padding: 0 8px;
        }

        .invoice-tab {
          display: inline-flex;
          align-items: center;
          gap: 6px;

          padding: 8px 12px;
          font-size: 13px;
          font-weight: 500;

          color: #6b7280;
          background: transparent;

          border: none;
          border-bottom: 2px solid transparent;
          border-radius: 0;

          cursor: pointer;
          transition: all 0.15s ease;
        }

        .invoice-tab.active {
          color: #1e40af;
          border-bottom-color: #3b82f6;
        }

        .invoice-tab:hover:not(.active) {
          color: #374151;
        }

        .invoice-tab svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        .invoice-tab-content {
          padding-top: 16px;
        }

        .invoice-tab-panel {
          display: none;
        }

        .invoice-tab-panel.active {
          display: block;
        }
        
        .section-header {
          margin-bottom: 15px;
        }

        .section-title {
          font-size: 17px;       
          font-weight: 600;
          line-height: 1.3;
          margin-bottom: 4px;
        }

        .section-description {
          font-size: 12px;
          color: #6b7280;
        }

        .invoice-tabs-container {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }

        .project-form-card {
          padding-top: 12px;
        }

        .invoice-tabs {
          margin-bottom: 12px;
          padding-bottom: 4px;
        }

        .invoice-tab-content {
          padding-top: 20px; 
        }

        margin-top: 0;
        padding-top: 0;
      `}
    </style>

      <div className="page-container">

      {/* Main Content */}
      <div className="project-create-container">
        <form onSubmit={handleSubmit}>
          {/* Form Card with Tabs */}
          <div className="project-form-card">
            {/* Tab Navigation */}
            <div className="invoice-tabs-container">
              <div className="invoice-tabs">
                <button
                  type="button"
                  className={`invoice-tab ${activeTab === 0 ? 'active' : ''}`}
                  onClick={() => setActiveTab(0)}
                >
                  <Receipt size={16} />
                  Invoice Info
                </button>
                <button
                  type="button"
                  className={`invoice-tab ${activeTab === 1 ? 'active' : ''}`}
                  onClick={() => setActiveTab(1)}
                >
                  <User size={16} />
                  Client
                </button>
                <button
                  type="button"
                  className={`invoice-tab ${activeTab === 2 ? 'active' : ''}`}
                  onClick={() => setActiveTab(2)}
                >
                  <Target size={16} />
                  Project
                </button>
                <button
                  type="button"
                  className={`invoice-tab ${activeTab === 3 ? 'active' : ''}`}
                  onClick={() => setActiveTab(3)}
                >
                  <FileText size={16} />
                  Items
                </button>
                <button
                  type="button"
                  className={`invoice-tab ${activeTab === 4 ? 'active' : ''}`}
                  onClick={() => setActiveTab(4)}
                >
                  <DollarSign size={16} />
                  Summary
                </button>
                <button
                  type="button"
                  className={`invoice-tab ${activeTab === 5 ? 'active' : ''}`}
                  onClick={() => setActiveTab(5)}
                >
                  <FileText size={16} />
                  Additional
                </button>
              </div>
              
              {/* Tab Content */}
              <div className="invoice-tab-content">
                {/* Tab 1: Invoice Information */}
                <div className={`invoice-tab-panel ${activeTab === 0 ? 'active' : ''}`}>
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Invoice Information</h3>
                      <p className="section-description">Basic information about the invoice</p>
                    </div>
                    
                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Invoice Number *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.invoiceNumber}
                          onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                          required
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Invoice Date *</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.date}
                          onChange={(e) => handleInputChange('date', e.target.value)}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Due Date *</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.dueDate}
                          onChange={(e) => handleInputChange('dueDate', e.target.value)}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={formData.status}
                          onChange={(e) => handleInputChange('status', e.target.value)}
                        >
                          <option value="draft">Draft</option>
                          <option value="pending">Pending</option>
                          <option value="sent">Sent</option>
                          <option value="paid">Paid</option>
                          <option value="overdue">Overdue</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tab 2: Client Information */}
                <div className={`invoice-tab-panel ${activeTab === 1 ? 'active' : ''}`}>
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Client Information</h3>
                      <p className="section-description">Details about the client and billing address</p>
                    </div>
                    
                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Client *</label>
                        <select
                          className="form-select"
                          value={formData.client}
                          onChange={(e) => handleClientChange(e.target.value)}
                          required
                        >
                          <option value="">Select Client</option>
                          {clients.map(client => (
                            <option key={client.id} value={client.id}>
                              {client.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Client Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.clientName}
                          onChange={(e) => handleInputChange('clientName', e.target.value)}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          value={formData.clientEmail}
                          onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Phone</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.clientPhone}
                          onChange={(e) => handleInputChange('clientPhone', e.target.value)}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Client Address</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.clientAddress}
                        onChange={(e) => handleInputChange('clientAddress', e.target.value)}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Tab 3: Project Information */}
                <div className={`invoice-tab-panel ${activeTab === 2 ? 'active' : ''}`}>
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Project Information</h3>
                      <p className="section-description">Project details and description</p>
                    </div>
                    
                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Project *</label>
                        <select
                          className="form-select"
                          value={formData.project}
                          onChange={(e) => handleInputChange('project', e.target.value)}
                          required
                        >
                          <option value="">Select Project</option>
                          {Array.isArray(projects) &&
                          projects.map(project => (
                          <option key={project._id || project.id} value={project._id || project.id}>
                            {project.name}
                          </option>
                        ))}
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Description</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          placeholder="Brief description of the work performed"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tab 4: Invoice Items */}
                <div className={`invoice-tab-panel ${activeTab === 3 ? 'active' : ''}`}>
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Invoice Items</h3>
                      <p className="section-description">Line items for the invoice</p>
                    </div>
                    
                    <div className="invoice-items">
                      {formData.items.map((item, index) => (
                        <div key={item.id} className="invoice-item-row">
                          <div className="row">
                            <div className="col-md-5">
                              <div className="form-group">
                                <label className="form-label">Description *</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={item.description}
                                  onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                                  placeholder="Service description"
                                  required
                                />
                              </div>
                            </div>
                            
                            <div className="col-md-2">
                              <div className="form-group">
                                <label className="form-label">Quantity</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={item.quantity}
                                  onChange={(e) => handleItemChange(item.id, 'quantity', parseFloat(e.target.value))}
                                  min="0"
                                  step="0.25"
                                />
                              </div>
                            </div>
                            
                            <div className="col-md-2">
                              <div className="form-group">
                                <label className="form-label">Rate ($)</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={item.rate}
                                  onChange={(e) => handleItemChange(item.id, 'rate', parseFloat(e.target.value))}
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            </div>
                            
                            <div className="col-md-2">
                              <div className="form-group">
                                <label className="form-label">Amount ($)</label>
                                <input
                                  type="number"
                                  className="form-control"
                                  value={item.amount}
                                  readOnly
                                  style={{ backgroundColor: '#f8f9fa' }}
                                />
                              </div>
                            </div>
                            
                            <div className="col-md-1">
                              <div className="form-group">
                                <label className="form-label">&nbsp;</label>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleRemoveItem(item.id)}
                                  disabled={formData.items.length === 1}
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      <div className="d-flex justify-content-start mt-3">
                        <button
                          type="button"
                          className="btn btn-outline d-flex align-items-center"
                          onClick={handleAddItem}
                        >
                          <Plus size={16} />
                          Add Item
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tab 5: Financial Summary */}
                <div className={`invoice-tab-panel ${activeTab === 4 ? 'active' : ''}`}>
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Financial Summary</h3>
                      <p className="section-description">Invoice totals and tax information</p>
                    </div>
                    
                    <div className="form-grid-4">
                      <div className="form-group">
                        <label className="form-label">Subtotal ($)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.subtotal.toFixed(2)}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Tax Rate (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.taxRate}
                          onChange={(e) => handleInputChange('taxRate', parseFloat(e.target.value))}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Tax Amount ($)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.taxAmount.toFixed(2)}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Total ($)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.total.toFixed(2)}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa', fontWeight: '600', fontSize: '16px' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tab 6: Additional Information */}
                <div className={`invoice-tab-panel ${activeTab === 5 ? 'active' : ''}`}>
                  <div className="project-form-section">
                    <div className="section-header">
                      <h3 className="section-title">Additional Information</h3>
                      <p className="section-description">Notes, terms, and payment information</p>
                    </div>
                    
                    <div className="form-grid-2">
                      <div className="form-group">
                        <label className="form-label">Notes</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={formData.notes}
                          onChange={(e) => handleInputChange('notes', e.target.value)}
                          placeholder="Additional notes or comments..."
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Payment Terms</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={formData.terms}
                          onChange={(e) => handleInputChange('terms', e.target.value)}
                          placeholder="Payment terms and conditions..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="project-form-actions">
            <button
              type="button"
              className="btn-outline-cam"
              onClick={() => navigate('/projects/time-tracking/billing')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-cam"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Creating Invoice...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Create Invoice
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      </div>
    </>
  );
};

export default CreateInvoice;
